import { useEffect, useMemo, useState } from "react";
import UiIcon from "../components/UiIcon";
import { apiRequest, invalidateCached } from "./api";
import { useResource, periodQuery } from "./hooks";
import { experienceCopy, formatDay, formatDateTime, formatNumber, formatPercent, dueLabel, daysFromToday } from "./copy";

const MANAGED = { "data-cvi-i18n": "managed" };

// ── shared pieces ───────────────────────────────────────────────────────────

export function PanelHead({ eyebrow, title, titleId, aside }) {
    return <div className="cvi-manager-section-head cvi-xp-head">
        <div>
            {eyebrow && <span>{eyebrow}</span>}
            <h2 id={titleId}>{title}</h2>
        </div>
        {aside}
    </div>;
}

export function PanelError({ language, onRetry }) {
    const c = experienceCopy(language).common;
    return <div className="cvi-xp-empty is-error" role="status">
        <span>{c.error}</span>
        {onRetry && <button type="button" className="cvi-xp-link" onClick={onRetry}>{c.retry}</button>}
    </div>;
}

// Dashboard chips use the shared workspace status style so a status reads the same on every surface.
function Chip({ tone = "neutral", children }) {
    return <span className={`cvi-ws-status is-${tone}`}><i aria-hidden="true" />{children}</span>;
}

const STATE_TONE = { overdue: "danger", due_soon: "warning", my_action: "primary", waiting: "neutral", info: "info" };
const CATEGORY_ICON = {
    routine: "self", activity: "activity", contribution: "activity", peer: "peer", indicator: "target", goal: "target",
    approval: "inbox", support: "warning", review: "flag", "check-in": "schedule",
};

// ── Smart next steps (inside the existing "Needs my attention" card) ────────

const STATE_ORDER = ["overdue", "due_soon", "my_action", "waiting", "info"];
const ACTIONABLE = ["overdue", "due_soon", "my_action"];

export function buildNextSteps(payload) {
    if (!payload) return [];
    const seen = new Set();
    const all = [...(payload.tasks || []), ...(payload.actions || []), ...(payload.waiting || []), ...(payload.updates || [])]
        .filter(task => { if (seen.has(task.id)) return false; seen.add(task.id); return true; });
    const rank = task => STATE_ORDER.indexOf(task.state || "my_action");
    return all.sort((a, b) => rank(a) - rank(b)
        || String(a.dueAt || "9999").localeCompare(String(b.dueAt || "9999"))
        || String(a.title).localeCompare(String(b.title)));
}

// The four summary tiles group the same steps the list shows, so every number on the card
// can be traced to rows below it. "To complete" is every other step that needs the user's action.
export const SUMMARY_TYPES = {
    feedback: ["PEER_FEEDBACK"],
    indicators: ["INDICATOR_ENTRY", "INDICATOR_APPROVAL"],
    support: ["SUPPORT_RESPONSE", "SUPPORT_REQUESTED"],
};
const summaryBucket = task => Object.keys(SUMMARY_TYPES).find(key => SUMMARY_TYPES[key].includes(task.type)) || "items";
const isActionable = task => ACTIONABLE.includes(task.state || "my_action");

export function summarizeSteps(steps = []) {
    const counts = { items: 0, feedback: 0, indicators: 0, support: 0, supportMine: 0, actionable: 0, waiting: 0 };
    for (const task of steps) {
        if (task.type === "SUPPORT_REQUESTED") {
            // One row stands for every open challenge the user raised; it waits on their manager.
            const raised = Number(task.params?.count) || 1;
            counts.support += raised;
            counts.supportMine += raised;
            counts.waiting += 1;
            continue;
        }
        if (!isActionable(task)) { if ((task.state || "") === "waiting") counts.waiting += 1; continue; }
        counts.actionable += 1;
        counts[summaryBucket(task)] += 1;
    }
    return counts;
}

function useMediaQuery(query) {
    const get = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;
    const [matches, setMatches] = useState(get);
    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
        const list = window.matchMedia(query);
        const onChange = () => setMatches(list.matches);
        onChange();
        list.addEventListener?.("change", onChange);
        return () => list.removeEventListener?.("change", onChange);
    }, [query]);
    return matches;
}

// Desktop keeps "Needs my attention" at a fixed height, so every step is listed inside a bounded,
// scrollable region. Narrower layouts flow with the page and show a short preview instead.
export const FIXED_ROW_QUERY = "(min-width: 1181px)";

export function NextStepsList({ language, periodId, onOpenTarget, summary = [], onCount, limit = 4 }) {
    const t = experienceCopy(language);
    const { data, loading, error, reload } = useResource(periodId ? `/api/workflow/tasks${periodQuery(periodId)}&include=dashboard` : null);
    const [expanded, setExpanded] = useState(false);
    const fixedLayout = useMediaQuery(FIXED_ROW_QUERY);
    const steps = useMemo(() => buildNextSteps(data), [data]);
    const counts = useMemo(() => summarizeSteps(steps), [steps]);
    const actionable = counts.actionable;
    useEffect(() => { if (data) onCount?.(actionable); }, [data, actionable, onCount]);
    const visible = fixedLayout || expanded ? steps : steps.slice(0, limit);
    const label = task => {
        const params = task.params || {};
        const title = t.nextSteps.types[task.type]?.(params, task) || task.title;
        const detail = t.nextSteps.details[task.type]?.(params, task) ?? task.description;
        return { title, detail };
    };
    // Tiles show server-backed counts once the step list has loaded; until then the caller's counts.
    const tileCount = item => (data ? counts[item.id] ?? item.count : item.count);
    const openTile = item => {
        const match = data && steps.find(task => (item.id === "items" ? isActionable(task) && summaryBucket(task) === "items" : summaryBucket(task) === item.id));
        if (match?.actionTarget) onOpenTarget?.(match.actionTarget, match);
        else item.onClick?.();
    };
    return <div className={`cvi-xp-nextsteps${fixedLayout ? " is-fixed" : ""}`} {...MANAGED}>
        {summary.length > 0 && <div className="cvi-xp-summary" role="group" aria-label={t.nextSteps.summaryHelp}>
            {summary.map(item => {
                const count = tileCount(item);
                return <button type="button" key={item.id} className={`cvi-xp-summary-item${count ? "" : " is-zero"}`} onClick={() => openTile(item)}>
                    <span className="cvi-xp-summary-icon" aria-hidden="true"><UiIcon name={item.icon} size={14} /></span>
                    <span className="cvi-xp-summary-label">{item.label}</span>
                    <b>{formatNumber(count, language)}</b>
                </button>;
            })}
        </div>}
        <div className="cvi-xp-steps-region" role="region" aria-label={t.nextSteps.region} tabIndex={fixedLayout && steps.length > 0 ? 0 : undefined}>
            {loading && !data ? <div className="cvi-xp-skeleton" aria-busy="true"><span className="cvi-xp-sr">{t.common.loading}</span><i /><i /><i /></div>
                : error && !data ? <PanelError language={language} onRetry={reload} />
                : steps.length === 0 ? <div className="cvi-xp-empty is-calm"><UiIcon name="check" size={16} /><div><strong>{t.nextSteps.empty}</strong><small>{t.nextSteps.emptyHelp}</small></div></div>
                : <ul className="cvi-xp-steplist">
                    {visible.map((task, index) => {
                        const { title, detail } = label(task);
                        const state = task.state || "my_action";
                        const showGroup = index === 0 || (visible[index - 1].state || "my_action") !== state;
                        const due = task.dueAt ? (state === "info" ? formatDateTime(task.dueAt, language) : dueLabel(task.dueAt, language)) : null;
                        return <li key={task.id}>
                            {showGroup && <div className="cvi-xp-group">{t.nextSteps.groups[state]}</div>}
                            <button type="button" className={`cvi-xp-step is-${state}`} onClick={() => onOpenTarget?.(task.actionTarget, task)}>
                                <span className="cvi-xp-step-icon" aria-hidden="true"><UiIcon name={CATEGORY_ICON[task.category] || CATEGORY_ICON[task.module] || "activity"} size={15} /></span>
                                <span className="cvi-xp-step-main">
                                    <strong>{title}</strong>
                                    {detail && <small>{detail}</small>}
                                    {(due || state === "overdue" || state === "due_soon") && <span className="cvi-xp-step-meta">
                                        {(state === "overdue" || state === "due_soon") && <Chip tone={STATE_TONE[state]}>{t.states[state]}</Chip>}
                                        {due && <small className={state === "overdue" ? "is-danger" : ""}>{due}</small>}
                                    </span>}
                                </span>
                                <span className="cvi-xp-step-go" aria-hidden="true">›</span>
                            </button>
                        </li>;
                    })}
                </ul>}
        </div>
        {data && steps.length > 0 && <div className="cvi-xp-nextsteps-foot">
            <small>
                {t.nextSteps.needAction(actionable)}
                {counts.waiting > 0 && ` · ${t.nextSteps.waitingCount(counts.waiting)}`}
                {counts.supportMine > 0 && <span className="cvi-xp-sr"> {t.nextSteps.supportMine(counts.supportMine)}</span>}
            </small>
            {!fixedLayout && steps.length > limit && <button type="button" className="cvi-xp-link" aria-expanded={expanded} onClick={() => setExpanded(v => !v)}>
                {expanded ? t.common.showLess : `${t.common.viewAll} (${formatNumber(steps.length, language)})`}
            </button>}
        </div>}
    </div>;
}

// ── Review cycle tracker ────────────────────────────────────────────────────

export function stageLabel(stage, language) {
    const t = experienceCopy(language);
    const custom = language === "fr" ? (stage.labelFr || stage.labelEn) : (stage.labelEn || stage.labelFr);
    return custom || t.cycle.stage[stage.stageType] || t.cycle.stage.CUSTOM;
}

function StageMarker({ state, current }) {
    if (state === "COMPLETED") return <span className="cvi-xp-stage-marker is-done" aria-hidden="true"><UiIcon name="check" size={11} strokeWidth={3} /></span>;
    if (state === "OVERDUE") return <span className="cvi-xp-stage-marker is-overdue" aria-hidden="true">!</span>;
    return <span className={`cvi-xp-stage-marker${current ? " is-current" : ""}${state === "NOT_APPLICABLE" ? " is-muted" : ""}`} aria-hidden="true" />;
}

export function ReviewCycleTracker({ language, periodId, onOpenTarget }) {
    const t = experienceCopy(language);
    const { data, error, reload } = useResource(periodId ? `/api/experience/review-cycle${periodQuery(periodId)}` : null);
    const [ackOpen, setAckOpen] = useState(false);
    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);
    const [ackError, setAckError] = useState("");
    if (error && !data) return null;
    if (!data?.configured || !data.stages?.length) return null;

    const actionFor = stage => {
        if (stage.state === "COMPLETED" || stage.state === "NOT_APPLICABLE") return null;
        if (stage.stageType === "SELF_ASSESSMENT") return { label: t.cycle.action.SELF_ASSESSMENT, run: () => onOpenTarget?.("routine-self") };
        if (stage.stageType === "PEER_FEEDBACK") return { label: t.cycle.action.PEER_FEEDBACK, run: () => onOpenTarget?.("peer-feedback") };
        if (stage.stageType === "FINAL_ACKNOWLEDGEMENT" && stage.detail?.finalized) return { label: t.cycle.action.FINAL_ACKNOWLEDGEMENT, run: () => setAckOpen(true) };
        return null;
    };
    const acknowledge = async () => {
        setSaving(true); setAckError("");
        try {
            await apiRequest(`/api/experience/review-cycle/${encodeURIComponent(data.period.id)}/acknowledge`, { method: "POST", body: { comment } });
            invalidateCached("/api/workflow");
            setAckOpen(false); setComment("");
            reload();
        } catch (e) { setAckError(e.message); } finally { setSaving(false); }
    };
    const current = data.current;
    return <section className="cvi-manager-attention-card cvi-xp-card cvi-xp-cycle" aria-labelledby="xp-cycle-title" {...MANAGED}>
        <div className="cvi-manager-section-head cvi-xp-head">
            <div>
                <span>{t.cycle.eyebrow}</span>
                <h2 id="xp-cycle-title">{t.cycle.title}</h2>
            </div>
            {data.progress?.total > 0 && <small className="cvi-xp-head-note">{t.cycle.progress(data.progress.completed, data.progress.total)}</small>}
        </div>
        <ol className="cvi-xp-stages" style={{ "--xp-stage-count": data.stages.length }}>
            {data.stages.map(stage => {
                const action = stage.isCurrent ? actionFor(stage) : null;
                const detail = stage.detail && t.cycle.detail[stage.stageType]?.(stage.detail);
                return <li key={stage.id} className={`cvi-xp-stage is-${stage.state.toLowerCase()}${stage.isCurrent ? " is-current" : ""}`} aria-current={stage.isCurrent ? "step" : undefined}>
                    <div className="cvi-xp-stage-track"><StageMarker state={stage.state} current={stage.isCurrent} /><span className="cvi-xp-stage-line" aria-hidden="true" /></div>
                    <strong>{stageLabel(stage, language)}</strong>
                    <small className="cvi-xp-stage-dates">
                        {stage.dueDate ? `${t.common.due} ${formatDay(stage.dueDate, language)}` : stage.startDate ? formatDay(stage.startDate, language) : t.common.noDeadline}
                    </small>
                    <span className="cvi-xp-stage-state">
                        {t.cycle.state[stage.state]}
                        {stage.daysRemaining != null && stage.state !== "OVERDUE" && stage.isCurrent && ` · ${stage.daysRemaining === 1 ? t.common.dayLeft : stage.daysRemaining === 0 ? t.common.today : t.common.daysLeft(stage.daysRemaining)}`}
                        {stage.state === "OVERDUE" && stage.daysRemaining != null && ` · ${t.common.daysOverdue(Math.abs(stage.daysRemaining))}`}
                    </span>
                    {stage.isCurrent && detail && <small className="cvi-xp-stage-detail">{detail}</small>}
                    {stage.isCurrent && stage.stageType === "MANAGER_REVIEW" && stage.state !== "COMPLETED" && <small className="cvi-xp-stage-detail">{t.cycle.managerWaiting}</small>}
                    {stage.stageType === "FINAL_ACKNOWLEDGEMENT" && stage.state === "COMPLETED" && stage.completedAt && <small className="cvi-xp-stage-detail">{t.cycle.ack.done(formatDay(stage.completedAt, language))}</small>}
                    {action && <button type="button" className="cvi-xp-button is-small" onClick={action.run}>{action.label}</button>}
                </li>;
            })}
        </ol>
        {!current && <small className="cvi-xp-head-note">{t.cycle.allDone}</small>}
        {ackOpen && <div className="cvi-xp-dialog" role="dialog" aria-modal="true" aria-labelledby="xp-ack-title">
            <div className="cvi-xp-dialog-card">
                <h3 id="xp-ack-title">{t.cycle.ack.title}</h3>
                <p>{t.cycle.ack.body}</p>
                <label className="cvi-xp-field"><span>{t.cycle.ack.comment}</span>
                    <textarea rows={3} value={comment} onChange={e => setComment(e.target.value)} maxLength={2000} />
                </label>
                {ackError && <p className="cvi-xp-error">{ackError}</p>}
                <div className="cvi-xp-dialog-actions">
                    <button type="button" className="cvi-xp-button is-secondary" onClick={() => setAckOpen(false)}>{t.common.cancel}</button>
                    <button type="button" className="cvi-xp-button" disabled={saving} onClick={acknowledge}>{saving ? t.common.saving : t.cycle.ack.submit}</button>
                </div>
            </div>
        </div>}
    </section>;
}

// ── Goals ──────────────────────────────────────────────────────────────────

const HEALTH_TONE = { ON_TRACK: "success", NEEDS_ATTENTION: "warning", AT_RISK: "danger", OVERDUE: "danger", NOT_ASSESSED: "neutral", COMPLETED: "success" };

// The dashboard links to the existing goals screen; it opens on the goal the person picked.
export const GOAL_FOCUS_KEY = "cvi_focus_goal";
export function rememberGoalFocus(goalId) {
    try { if (goalId) sessionStorage.setItem(GOAL_FOCUS_KEY, String(goalId)); } catch { /* storage unavailable */ }
}

export function GoalsPanel({ language, periodId, onOpenGoals }) {
    const t = experienceCopy(language);
    const { data, loading, error, reload } = useResource(periodId ? `/api/experience/goals${periodQuery(periodId)}` : null);
    const goals = (data?.goals || []).filter(goal => goal.status !== "CANCELLED");
    const shown = goals.slice(0, 4);
    const open = goal => { rememberGoalFocus(goal?.id); onOpenGoals?.(goal); };
    return <section className="cvi-manager-attention-card cvi-xp-card" aria-labelledby="xp-goals-title" {...MANAGED}>
        <div className="cvi-manager-section-head cvi-xp-head">
            <div><span>{t.goals.eyebrow}</span><h2 id="xp-goals-title">{t.goals.title}</h2></div>
            <button type="button" className="cvi-xp-link" onClick={() => onOpenGoals?.()}>{t.goals.manage}</button>
        </div>
        {loading && !data ? <div className="cvi-xp-skeleton" aria-busy="true"><span className="cvi-xp-sr">{t.common.loading}</span><i /><i /></div>
            : error && !data ? <PanelError language={language} onRetry={reload} />
            : shown.length === 0 ? <div className="cvi-xp-empty"><strong>{t.goals.empty}</strong><small>{t.goals.emptyHelp}</small></div>
            : <ul className="cvi-xp-goals">
                {shown.map(goal => {
                    const progress = Math.max(0, Math.min(100, Number(goal.progress) || 0));
                    const showHealth = goal.status === "ACTIVE" && goal.health && goal.health !== "NOT_APPLICABLE";
                    const expected = showHealth && goal.expectedProgress != null ? Math.max(0, Math.min(100, Number(goal.expectedProgress))) : null;
                    const tone = goal.status === "COMPLETED" ? "success" : showHealth ? HEALTH_TONE[goal.health] || "neutral" : "neutral";
                    return <li key={goal.id}>
                        <button type="button" className="cvi-xp-goal" onClick={() => open(goal)} aria-label={t.goals.open(goal.title)}>
                            <span className="cvi-xp-goal-top">
                                <strong>{goal.title}</strong>
                                <span className="cvi-xp-goal-pct">{formatPercent(progress, language)}</span>
                            </span>
                            <span className={`cvi-xp-bar is-${tone}`} role="img" aria-label={`${formatPercent(progress, language)} ${t.goals.complete}${expected != null ? ` · ${t.goals.expected(expected)}` : ""}`}>
                                <span style={{ width: `${progress}%` }} />
                                {expected != null && <i className="cvi-xp-bar-marker" style={{ insetInlineStart: `${expected}%` }} />}
                            </span>
                            <span className="cvi-xp-goal-meta">
                                <span className="cvi-xp-lifecycle"><span className="cvi-xp-sr">{t.goals.status}: </span>{t.goals.lifecycle[goal.status] || goal.status}</span>
                                {showHealth && <span className={`cvi-xp-health is-${HEALTH_TONE[goal.health] || "neutral"}`}>
                                    <i aria-hidden="true" /><span className="cvi-xp-sr">{t.goals.health_}: </span>{t.goals.health[goal.health]}
                                    {expected != null && <small>· {t.goals.expectedShort(formatNumber(Math.round(expected), language))}</small>}
                                </span>}
                                <span className="cvi-xp-goal-due">{goal.deadline ? `${t.common.due} ${formatDay(goal.deadline, language)}` : t.common.noDeadline}</span>
                            </span>
                        </button>
                    </li>;
                })}
            </ul>}
        {goals.length > shown.length && <button type="button" className="cvi-xp-link cvi-xp-more" onClick={() => onOpenGoals?.()}>{t.common.viewAll} ({formatNumber(goals.length, language)})</button>}
    </section>;
}

// ── Additional contributions ───────────────────────────────────────────────

// "submitted" is cumulative: every contribution that left draft, whatever its current status.
// approved / inReview / revision / rejected are mutually exclusive slices of it.
export function contributionCounts(activities = []) {
    const status = a => String(a.status || "").toLowerCase();
    return {
        submitted: activities.filter(a => status(a) !== "draft").length,
        approved: activities.filter(a => status(a) === "approved").length,
        inReview: activities.filter(a => ["pending_review", "resubmitted"].includes(status(a))).length,
        revision: activities.filter(a => status(a) === "revision_requested").length,
        rejected: activities.filter(a => status(a) === "rejected").length,
        drafts: activities.filter(a => status(a) === "draft").length,
    };
}

const CONTRIBUTION_TONE = { revision_requested: "warning", approved: "success", rejected: "danger", pending_review: "info", resubmitted: "info", draft: "neutral" };

export function ContributionsPanel({ language, activities = [], onView, onAdd }) {
    const t = experienceCopy(language);
    const counts = contributionCounts(activities);
    const touched = a => [a.approvedAt, a.rejectedAt, a.resubmittedAt, a.revisionRequestedAt, a.submittedAt, a.createdAt].filter(Boolean).sort().pop() || "";
    const latest = [...activities].sort((a, b) => touched(b).localeCompare(touched(a)))[0];
    const latestStatus = latest ? String(latest.status).toLowerCase() : null;
    const breakdown = [["approved", t.contributions.approved], ["inReview", t.contributions.inReview], ["revision", t.contributions.revision]];
    return <section className="cvi-manager-attention-card cvi-xp-card" aria-labelledby="xp-contrib-title" {...MANAGED}>
        <div className="cvi-manager-section-head cvi-xp-head">
            <div><span>{t.contributions.eyebrow}</span><h2 id="xp-contrib-title">{t.contributions.title}</h2></div>
            <button type="button" className="cvi-xp-link" onClick={onView}>{t.contributions.view}</button>
        </div>
        <div className="cvi-xp-stats">
            <div className="is-total">
                <strong>{formatNumber(counts.submitted, language)}</strong>
                <span>{t.contributions.totalSubmitted}</span>
                <small>{counts.rejected ? t.contributions.notApproved(counts.rejected) : t.contributions.totalHelp}</small>
            </div>
            <div className="cvi-xp-stats-split" role="group" aria-label={t.contributions.breakdown}>
                {breakdown.map(([key, label]) => <div key={key} className={key === "revision" && counts.revision ? "is-attention" : ""}>
                    <strong>{formatNumber(counts[key], language)}</strong><span>{label}</span>
                </div>)}
            </div>
        </div>
        {latest ? <button type="button" className="cvi-xp-latest" onClick={onView}>
            <span className="cvi-xp-latest-label">{t.contributions.latest}</span>
            <span className="cvi-xp-latest-main">
                <strong>{latest.title}</strong>
                {touched(latest) && <small>{t.contributions.updated(formatDay(touched(latest), language))}</small>}
            </span>
            <Chip tone={CONTRIBUTION_TONE[latestStatus] || "neutral"}>{t.contributions.status[latestStatus] || latest.status}</Chip>
        </button> : <div className="cvi-xp-empty"><small>{t.contributions.none}</small></div>}
        <div className="cvi-xp-actions">
            {counts.drafts > 0 && <button type="button" className="cvi-xp-link is-quiet" onClick={onView}>{t.contributions.draftCount(counts.drafts)}</button>}
            <button type="button" className="cvi-xp-button is-small" onClick={onAdd}><UiIcon name="plus" size={13} />{t.contributions.add}</button>
        </div>
    </section>;
}

// ── Optional modules: check-in, recognition, learning ─────────────────────

export function CheckInPanel({ language, onOpen }) {
    const t = experienceCopy(language);
    const { data } = useResource("/api/experience/check-ins");
    if (!data?.enabled) return null;
    const now = Date.now();
    const upcoming = [...(data.asEmployee || []), ...(data.asManager || [])]
        .filter(c => c.status === "SCHEDULED" && new Date(c.scheduledAt).getTime() >= now - 60 * 60 * 1000)
        .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
    const next = upcoming[0];
    const other = next ? (next.viewerRole === "MANAGER" ? next.employee?.name : next.manager?.name) : null;
    const topics = next ? next.items.filter(i => i.kind === "TOPIC") : [];
    return <section className="cvi-manager-attention-card cvi-xp-card cvi-xp-mini" aria-labelledby="xp-checkin-title" {...MANAGED}>
        <PanelHead eyebrow={t.checkIns.eyebrow} title={t.checkIns.title} titleId="xp-checkin-title" aside={<button type="button" className="cvi-xp-link" onClick={onOpen}>{t.checkIns.open}</button>} />
        {next ? <>
            <div className="cvi-xp-meeting">
                <strong>{formatDateTime(next.scheduledAt, language)}</strong>
                <small>{[other && t.checkIns.with(other), t.checkIns.minutes(next.durationMinutes), next.location].filter(Boolean).join(" · ")}</small>
            </div>
            {topics.length > 0 && <ul className="cvi-xp-bullets">{topics.slice(0, 3).map(item => <li key={item.id}>{item.text}</li>)}</ul>}
            <button type="button" className="cvi-xp-button is-small is-secondary" onClick={onOpen}>{t.checkIns.addTopic}</button>
        </> : <div className="cvi-xp-empty"><strong>{t.checkIns.none}</strong>{data.manager && <small>{t.checkIns.noneManager}</small>}</div>}
    </section>;
}

export function RecognitionPanel({ language, onOpen }) {
    const t = experienceCopy(language);
    const { data } = useResource("/api/experience/recognitions");
    if (!data?.enabled) return null;
    const latest = data.received?.[0];
    return <section className="cvi-manager-attention-card cvi-xp-card cvi-xp-mini" aria-labelledby="xp-recog-title" {...MANAGED}>
        <PanelHead eyebrow={t.recognition.eyebrow} title={t.recognition.title} titleId="xp-recog-title" aside={<small className="cvi-xp-head-note">{t.recognition.receivedCount(data.received?.length || 0, formatNumber(data.received?.length || 0, language))}</small>} />
        {latest ? <figure className="cvi-xp-quote">
            <blockquote>“{latest.message}”</blockquote>
            <figcaption>{[t.recognition.from(latest.giver?.name || "—"), formatDay(latest.createdAt, language), t.recognition.categories[latest.category]].filter(Boolean).join(" · ")}</figcaption>
        </figure> : <div className="cvi-xp-empty"><small>{t.recognition.empty}</small></div>}
        <div className="cvi-xp-actions">
            <button type="button" className="cvi-xp-link" onClick={onOpen}>{t.recognition.openAll}</button>
            {data.canGive && <button type="button" className="cvi-xp-button is-small is-secondary" onClick={onOpen}>{t.recognition.give}</button>}
        </div>
    </section>;
}

export function LearningPanel({ language, onOpen }) {
    const t = experienceCopy(language);
    const { data } = useResource("/api/experience/learning");
    if (!data?.enabled) return null;
    const target = data.annualTargetHours;
    const done = Number(data.completedHours) || 0;
    const inProgress = (data.items || []).filter(i => i.status === "IN_PROGRESS");
    return <section className="cvi-manager-attention-card cvi-xp-card cvi-xp-mini" aria-labelledby="xp-learning-title" {...MANAGED}>
        <PanelHead eyebrow={t.learning.eyebrow} title={t.learning.title} titleId="xp-learning-title" aside={<button type="button" className="cvi-xp-link" onClick={onOpen}>{t.learning.open}</button>} />
        <div className="cvi-xp-learning">
            <small>{t.learning.hoursThisYear(data.year)}</small>
            <strong>{target ? t.learning.ofTarget(formatNumber(done, language, { maximumFractionDigits: 1 }), formatNumber(target, language)) : `${formatNumber(done, language, { maximumFractionDigits: 1 })} h`}</strong>
            {target ? <span className="cvi-xp-bar" role="img" aria-label={t.learning.ofTarget(done, target)}><span style={{ width: `${Math.min(100, (done / target) * 100)}%` }} /></span> : <small>{t.learning.noTarget}</small>}
        </div>
        {inProgress.length > 0 ? <ul className="cvi-xp-bullets">{inProgress.slice(0, 2).map(item => <li key={item.id}>{item.title} <small>· {t.learning.status.IN_PROGRESS}</small></li>)}</ul>
            : (data.items || []).length === 0 && <div className="cvi-xp-empty"><small>{t.learning.empty}</small></div>}
    </section>;
}

// ── Challenges & support (enhanced existing section) ──────────────────────

export function ChallengeItem({ item, language, canUpdate = false, onStatus }) {
    const t = experienceCopy(language).challenges;
    const [busy, setBusy] = useState(false);
    const setStatus = async status => {
        setBusy(true);
        try { await onStatus?.(item, status); } finally { setBusy(false); }
    };
    return <article className="cvi-personal-challenge-item cvi-xp-challenge">
        <div className="cvi-personal-challenge-icon"><UiIcon name="warning" size={15} /></div>
        <div className="cvi-personal-challenge-main">
            <div className="cvi-personal-challenge-title">
                <strong>{item.title || t.categories[item.category] || t.sources[item.source]}</strong>
                <span className={`is-${String(item.status).toLowerCase()}`}>{t.status[item.status] || item.status}</span>
            </div>
            <small>{[t.sources[item.source], t.categories[item.category], item.ownerName && canUpdate ? item.ownerName : null, item.responsibleManagerName ? t.responsible(item.responsibleManagerName) : null, item.updatedAt ? formatDay(item.updatedAt, language) : null].filter(Boolean).join(" · ")}</small>
            {item.text && <p>{item.text}</p>}
            {(item.supportNeed || item.suggestedAction) && <div className="cvi-personal-challenge-support">
                {item.supportNeed && <div><span>{t.support}</span><strong>{item.supportNeed}</strong></div>}
                {item.suggestedAction && <div><span>{t.suggested}</span><strong>{item.suggestedAction}</strong></div>}
            </div>}
            {canUpdate && <div className="cvi-xp-challenge-actions" role="group" aria-label={t.setStatus}>
                {["OPEN", "IN_PROGRESS", "RESOLVED"].map(status => <button key={status} type="button" disabled={busy || item.status === status}
                    className={`cvi-xp-chip-button${item.status === status ? " is-active" : ""}`} onClick={() => setStatus(status)}>{t.status[status]}</button>)}
            </div>}
        </div>
    </article>;
}

export function ChallengesSection({ language, scope = "mine", title, subtitle, onRecord }) {
    const t = experienceCopy(language).challenges;
    const { data, loading, error, reload, setData } = useResource(`/api/experience/challenges?scope=${scope}`);
    const items = data?.items || [];
    const counts = data?.counts || { OPEN: 0, IN_PROGRESS: 0, RESOLVED: 0 };
    const update = async (item, status) => {
        await apiRequest(`/api/experience/challenges/${item.source}/${encodeURIComponent(item.id)}`, { method: "PATCH", body: { status } });
        setData(prev => prev ? { ...prev, items: prev.items.map(i => i.id === item.id && i.source === item.source ? { ...i, status } : i), counts: { ...prev.counts, [item.status]: Math.max(0, (prev.counts[item.status] || 0) - 1), [status]: (prev.counts[status] || 0) + 1 } } : prev);
        invalidateCached("/api/workflow");
    };
    return <section className="cvi-personal-challenges" id={scope === "team" ? "team-challenges" : "my-challenges"} {...MANAGED}>
        <div className="cvi-personal-challenges-head">
            <div>
                <h2>{title || t.title}</h2>
                {subtitle && <small className="cvi-xp-subtitle">{subtitle}</small>}
            </div>
            <div className="cvi-personal-challenge-counts">
                <div className={counts.OPEN ? "is-attention" : ""}><span>{t.open}</span><strong>{formatNumber(counts.OPEN || 0, language)}</strong></div>
                <div><span>{t.inProgress}</span><strong>{formatNumber(counts.IN_PROGRESS || 0, language)}</strong></div>
                <div><span>{t.resolved}</span><strong>{formatNumber(counts.RESOLVED || 0, language)}</strong></div>
                <div className="is-total"><span>{t.total}</span><strong>{formatNumber(data?.total ?? items.length, language)}</strong></div>
            </div>
        </div>
        {loading && !data ? <div className="cvi-xp-empty is-loading"><small>{experienceCopy(language).common.loading}</small></div>
            : error && !data ? <PanelError language={language} onRetry={reload} />
            : items.length === 0 ? <div className="cvi-personal-challenges-empty">
                <span aria-hidden="true"><UiIcon name="check" size={16} /></span>
                <div><strong>{scope === "team" ? t.teamEmpty : t.empty}</strong>{scope !== "team" && <small>{onRecord ? t.recordHelp : t.emptyHelp}</small>}</div>
                {scope !== "team" && onRecord && <button type="button" className="cvi-xp-button is-small is-secondary" onClick={onRecord}>{t.record}</button>}
            </div>
            : <div className="cvi-personal-challenges-list">
                {items.slice(0, scope === "team" ? 50 : 5).map(item => <ChallengeItem key={`${item.source}-${item.id}`} item={item} language={language} canUpdate={scope === "team"} onStatus={update} />)}
                {scope !== "team" && onRecord && <button type="button" className="cvi-xp-link cvi-xp-more" onClick={onRecord}><UiIcon name="plus" size={13} />{t.record}</button>}
            </div>}
    </section>;
}

export function PrivacyFooterLink({ language, onOpen }) {
    const t = experienceCopy(language).privacy;
    return <div className="cvi-xp-footer" {...MANAGED}>
        <button type="button" className="cvi-xp-link" onClick={onOpen}><UiIcon name="shield" size={13} />{t.link}</button>
    </div>;
}

export { daysFromToday };

// ── Team review cycle (manager "My Team" view) ─────────────────────────────

export function TeamReviewCycleCard({ language, periodId }) {
    const t = experienceCopy(language);
    const { data } = useResource(periodId ? `/api/experience/review-cycle/team${periodQuery(periodId)}` : null);
    const [showAll, setShowAll] = useState(false);
    if (!data?.configured || !data.stages?.length || !data.members?.length) return null;
    const fr = language === "fr";
    const members = showAll ? data.members : data.members.slice(0, 6);
    const labels = fr
        ? { title: "Cycle d’évaluation de l’équipe", done: "terminées", active: "en cours", overdue: "en retard", member: "Personne", step: "Étape en cours", none: "Toutes les étapes terminées" }
        : { title: "Team review cycle", done: "completed", active: "in progress", overdue: "overdue", member: "Person", step: "Current step", none: "All steps complete" };
    return <section className="cvi-manager-attention-card cvi-xp-card" aria-labelledby="xp-team-cycle-title" {...MANAGED}>
        <div className="cvi-manager-section-head cvi-xp-head">
            <div><span>{t.cycle.eyebrow}</span><h2 id="xp-team-cycle-title">{labels.title}</h2></div>
            <small className="cvi-xp-head-note">{formatNumber(data.members.length, language)} {fr ? "personnes" : "people"}</small>
        </div>
        <div className="cvi-xp-team-stages" style={{ "--xp-stage-count": data.stages.length }}>
            {data.stages.map(stage => {
                const tracked = stage.completed + stage.active + stage.overdue + stage.upcoming;
                return <div key={stage.id} className="cvi-xp-team-stage">
                    <strong>{stageLabel(stage, language)}</strong>
                    <small>{stage.dueDate ? `${t.common.due} ${formatDay(stage.dueDate, language)}` : t.common.noDeadline}</small>
                    <span className="cvi-xp-bar is-stacked" role="img" aria-label={`${stageLabel(stage, language)}: ${stage.completed} ${labels.done}, ${stage.active} ${labels.active}, ${stage.overdue} ${labels.overdue}`}>
                        {tracked > 0 && <>
                            <span className="is-done" style={{ width: `${(stage.completed / tracked) * 100}%` }} />
                            <span className="is-active" style={{ width: `${(stage.active / tracked) * 100}%` }} />
                            <span className="is-overdue" style={{ width: `${(stage.overdue / tracked) * 100}%` }} />
                        </>}
                    </span>
                    <small>{stage.completed} {labels.done} · {stage.active} {labels.active}{stage.overdue ? ` · ${stage.overdue} ${labels.overdue}` : ""}</small>
                </div>;
            })}
        </div>
        <div className="cvi-xp-table-wrap"><table className="cvi-xp-table">
            <thead><tr><th>{labels.member}</th><th>{labels.step}</th><th><span className="cvi-xp-sr">{t.states.overdue}</span></th></tr></thead>
            <tbody>{members.map(member => <tr key={member.id}>
                <td><strong>{member.name}</strong></td>
                <td>{member.current ? `${stageLabel(member.current, language)} · ${t.cycle.state[member.current.state]}` : labels.none}</td>
                <td>{member.overdueCount > 0 && <Chip tone="danger">{t.states.overdue}</Chip>}</td>
            </tr>)}</tbody>
        </table></div>
        {data.members.length > 6 && <button type="button" className="cvi-xp-link cvi-xp-more" onClick={() => setShowAll(v => !v)}>{showAll ? t.common.showLess : `${t.common.viewAll} (${data.members.length})`}</button>}
    </section>;
}
